import { useState } from "react";
import { useTranslation } from "react-i18next";
import Payment from "./Payment";
import RecipientDetail from "./RecipientDetail";
import * as CUSTOM from "../../../../utils/helper/custom";
import { useAppFont } from "utils/helper/useAppFont";
import globalRequest from "../../../../utils/global/globalRequest";
import * as APIS from "../../../../utils/helper/Enum";
import { changeLoader } from "../../../../redux/reducers/loader";
import { useDispatch } from "react-redux";

const MakePaymentWallet = ({ orderData, setIsGiftPayment, setOrderDetails }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { interClass, headingClass } = useAppFont();
  const [couponCode, setCouponCode] = useState("");
  const [couponCodeError, setCouponCodeError] = useState("");
  const [couponData, setCouponData] = useState({
    isApplied: false,
    couponCode: "",
    couponCodeId: "",
    discount: 0,
  });

  const applyCouponCodeForGift = async () => {
    const code = couponCode.trim();
    if (!code) return;

    dispatch(changeLoader(true));
    setCouponCodeError("");
    try {
      let response = await globalRequest(
        APIS.COUPON.APPLY_COUPON_CODE_FOR_GIFT,
        "get",
        {},
        { params: { coupon_code: code, order_id: orderData?.id } },
        true
      );
      response = response?.data;
      if (response?.status === "SUCCESS") {
        const discount = Number(response?.data?.coupon_discount_amount) || 0;
        setCouponData({
          isApplied: true,
          couponCode: code,
          couponCodeId: response?.data?.data?.id,
          discount,
        });
        setOrderDetails((prev) => ({
          ...prev,
          orderData: {
            ...prev?.orderData,
            priceDetails: {
              ...prev?.orderData?.priceDetails,
              total_amount:
                Number(prev?.orderData?.priceDetails?.total_amount || 0) -
                discount,
            },
          },
        }));
        CUSTOM.trackCouponApplied({ coupon_code: code });
      } else {
        setCouponCodeError(response?.message || "Unable to apply coupon.");
      }
    } catch (error) {
      setCouponCodeError(error?.message || "Unable to apply coupon. Please try again.");
    } finally {
      dispatch(changeLoader(false));
    }
  };

  const removeCouponCode = () => {
    const discount = Number(couponData?.discount) || 0;
    setOrderDetails((prev) => ({
      ...prev,
      orderData: {
        ...prev?.orderData,
        priceDetails: {
          ...prev?.orderData?.priceDetails,
          total_amount:
            Number(prev?.orderData?.priceDetails?.total_amount || 0) +
            discount,
        },
      },
    }));
    setCouponData({
      isApplied: false,
      couponCode: "",
      couponCodeId: "",
      discount: 0,
    });
    setCouponCode("");
    setCouponCodeError("");
  };

  const isGiftOrder =
    orderData?.orderType === "gift" ||
    orderData?.orderType === "custom_cake_gift";

  return (
    <>
      <div className={`w-full ${interClass}`}>

        {/* ── Back + Order Header ─────────────────────────────────────────── */}
        <div className="flex flex-row items-center gap-3 mb-5">
          <button
            className="flex flex-row items-center gap-1.5 text-gray-600 hover:text-black-900 transition-colors"
            onClick={() => setIsGiftPayment(false)}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span className={`text-sm ${interClass} text-gray-600`}>{t("back") || "Back"}</span>
          </button>
        </div>

        {/* ── Order number + type + date ── */}
        <div className="flex flex-row items-center gap-3 mb-6 flex-wrap">
          <span className={`font-semibold text-[24px] text-[#1A1A1A] leading-[32px] text-center ${interClass} block`}>
            {orderData?.orderNumber ? `${orderData.orderNumber}` : ""}
            <span className="font-normal text-gray-500 text-sm ml-1">
              ({orderData?.orderDetails?.length || 0} {t("items") || "items"})
            </span>
          </span>

          {/* Gift order badge */}
          {orderData?.orderType === "gift" || orderData?.orderType === "custom_cake_gift" ? (
            <div className="inline-flex items-center gap-1.5 bg-purple-50 border border-purple-200 rounded-full px-2.5 py-0.5">
              <svg className="w-3 h-3 text-purple-700" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z" clipRule="evenodd" />
                <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
              </svg>
              <span className={t`ext-xs font-medium text-purple-700 ${interClass}`}>
                {t("giftOrder") || "Gift Order"}
              </span>
            </div>
          ) : null}

          <span className={`text-sm text-gray-400 ${interClass}`}>
            {orderData?.createdAt
              ? CUSTOM.changeDateFormat(orderData.createdAt, "MMMM dd, yyyy")
              : ""}
          </span>
        </div>

        {/* ── Two-column layout: Payment | Order Details ── */}
        <div className="flex flex-row md:flex-col w-full gap-5">

          {/* ── LEFT: Payment Methods ── */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 pt-5 pb-1 border-b border-gray-100">
                <h2 className={`text-[24px] font-medium text-[#1A1A1A] leading-[32px] ${headingClass}`}>
                  {t("paymentMethod") || "Payment Methods"}
                </h2>
              </div>
              <div className="p-5">
                <Payment
                  setIsGiftPayment={setIsGiftPayment}
                  orderData={orderData}
                  couponData={couponData}
                />
              </div>
            </div>
          </div>

          {/* ── RIGHT: Order Details ── */}
          {orderData?.orderType === "gift" || orderData?.orderType === "custom_cake_gift" || orderData?.orderType === "custom_cake" ? (
            <div className="w-full max-w-[340px] md:max-w-full flex-shrink-0">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 pt-5 pb-1 border-b border-gray-100">
                  <h2 className={`text-base font-semibold text-gray-900 ${interClass}`}>
                    {t("orderDetails") || "Order Details"}
                  </h2>
                </div>
                <div className="p-5">
                  <RecipientDetail
                    details={orderData}
                    couponData={isGiftOrder ? couponData : null}
                    couponCode={isGiftOrder ? couponCode : ""}
                    setCouponCode={isGiftOrder ? setCouponCode : null}
                    couponCodeError={isGiftOrder ? couponCodeError : ""}
                    applyCouponCode={
                      isGiftOrder ? applyCouponCodeForGift : null
                    }
                    removeCouponCode={isGiftOrder ? removeCouponCode : null}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/*
        ── OLD LAYOUT (commented, not removed) ────────────────────────────────
        <div className="">
          <div className="flex flex-col w-full">
            <Text className="text-2xl md:text-[22px] text-black-900 sm:text-xl mb-7" size="txtnunitoSemiBold24">
              {t("payment")}
            </Text>
            <div className="flex flex-row md:flex-col w-full gap-6 mb-8">
              <div className="flex flex-row md:flex-col w-full gap-6 w-full pl-7 rtl:pl-0 rtl:pr-7 relative">
                <div className="relative bg-gray-100_01 py-[18px] px-6 xs:px-4 xs:mb-[148px] w-full">
                  <div className="absolute bg-red-100 flex h-9 items-center justify-center -left-7 rtl:-left-[auto] rtl:-right-7 p-1.5 top-6 w-9 z-[1]">
                    <Img className="h-6" src="/images/account_balance_wallet.svg" alt="account_balance_wallet" />
                  </div>
                  <div className="absolute border-l-2 mt-2 border-gray-300 border-dashed w-px -left-3 rtl:-left-[auto] rtl:-right-3 z-[0]"></div>
                  <Payment setIsGiftPayment={setIsGiftPayment} orderData={orderData} setOrderDetails={setOrderDetails} />
                </div>
              </div>
              {orderData?.orderType == 'gift' ? <RecipientDetail details={orderData} /> : null}
            </div>
          </div>
        </div>

        ── OLD two-col layout ─────────────────────────────────────────────────
        <div className="flex flex-row md:flex-col w-full gap-6">
          <div className="flex-1 min-w-0">
            <div className="bg-white-A700 rounded-2xl p-6 xs:p-4 w-full" style={{ boxShadow: "0px 2px 16px rgba(0,0,0,0.06)" }}>
              <Text className="font-nunitosemibold text-lg text-black-900 mb-5">{t("paymentMethods") || "Payment Methods"}</Text>
              <Payment setIsGiftPayment={setIsGiftPayment} orderData={orderData} setOrderDetails={setOrderDetails} />
            </div>
          </div>
          {orderData?.orderType === "gift" || orderData?.orderType === "custom_cake_gift" ? (
            <div className="w-full max-w-[320px] md:max-w-full flex-shrink-0">
              <div className="bg-white-A700 rounded-2xl p-6 xs:p-4 w-full" style={{ boxShadow: "0px 2px 16px rgba(0,0,0,0.06)" }}>
                <Text className="font-nunitosemibold text-base text-black-900 mb-4">{t("orderDetails") || "Order Details"}</Text>
                <RecipientDetail details={orderData} />
              </div>
            </div>
          ) : null}
        </div>
      */}
    </>
  );
};

export default MakePaymentWallet;
